import { useEffect, useRef } from 'react';
import { useSocket } from '@/core/hooks/useSocket';
import { baseApi } from '@/api/core/baseApi';
import { ConversationData, conversationQueriesApi, transformConversation } from '@/api/features/conversations';
import type { BackendConversation } from '@/api/features/conversations';
import { recentlyMutatedConversationIds } from '@/api/features/conversations/endpoints/mutations';
import { contactsCoreApi, recentlyUpdatedContactIds } from '@/api/features/contacts/endpoints/core';
import { store } from '@/core/store';
import { useAppDispatch, useAppSelector } from '@/core/store/hooks';
import {
  ServerEvents,
  type ConversationActivityPayload,
  type ConversationUpdatedPayload,
  type ContactConsentUpdatedPayload,
} from '@voley/shared-types/socket';

type CacheEntry = { endpointName?: string; originalArgs?: unknown; data?: unknown; status?: string };

/**
 * Iterate all getConversations + getInfiniteConversations cache entries.
 * The updater receives the draft and the originalArgs for that cache entry
 * so callers can branch on `view` or other query params.
 */
function forEachConversationCache(
  updater: (draft: { items: ConversationData[]; total?: number }, args: unknown) => void
) {
  const state = store.getState();
  const queries = (state[baseApi.reducerPath]?.queries ?? {}) as Record<string, CacheEntry | undefined>;
  Object.values(queries).forEach((entry) => {
    if (!entry) return;
    if (entry.endpointName === 'getConversations') {
      store.dispatch(
        conversationQueriesApi.util.updateQueryData(
          'getConversations',
          entry.originalArgs as Parameters<typeof conversationQueriesApi.endpoints.getConversations.initiate>[0],
          (draft) => { updater(draft as { items: ConversationData[]; total?: number }, entry.originalArgs); }
        )
      );
    } else if (entry.endpointName === 'getInfiniteConversations') {
      store.dispatch(
        conversationQueriesApi.util.updateQueryData(
          'getInfiniteConversations',
          entry.originalArgs as Parameters<typeof conversationQueriesApi.endpoints.getInfiniteConversations.initiate>[0],
          (draft) => { updater(draft as { items: ConversationData[]; total?: number }, entry.originalArgs); }
        )
      );
    }
  });
}

/**
 * Hook for handling global conversation events that should always be listened to,
 * regardless of whether a specific conversation is selected.
 * This ensures conversation list updates work immediately from app start.
 *
 * Socket events are batched via requestAnimationFrame to prevent per-event
 * re-renders when multiple events arrive in quick succession.
 */
export const useGlobalConversationEvents = () => {
  const { connected, on, off } = useSocket();
  const dispatch = useAppDispatch();
  const activeConversationId = useAppSelector((state) => state.messagingUi.currentConversationId);
  // Use refs to always have current values in socket callbacks (avoids stale closures)
  const activeConversationIdRef = useRef(activeConversationId);
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (!connected) return;

    // --- Batching infrastructure ---
    // Collect socket-driven operations during a frame, flush together in rAF
    // to prevent per-event re-renders when multiple events arrive rapidly.
    const pendingCreations: ConversationData[] = [];
    const pendingTags: Array<{ type: 'Conversations' | 'Contacts'; id: string }> = [];
    const pendingContactUpdates: ContactConsentUpdatedPayload[] = [];
    const pendingConversationUpdates: ConversationUpdatedPayload[] = [];
    let rafHandle = 0;

    function flush() {
      rafHandle = 0;
      const creations = pendingCreations.splice(0);
      const tags = pendingTags.splice(0);
      const contactUpdates = pendingContactUpdates.splice(0);
      const conversationUpdates = pendingConversationUpdates.splice(0);

      if (creations.length === 0 && tags.length === 0 && contactUpdates.length === 0 && conversationUpdates.length === 0) return;

      // 1. Batch-insert new conversations
      if (creations.length > 0) {
        forEachConversationCache((draft) => {
          if (!draft?.items) return;
          for (const data of creations) {
            if (!draft.items.some((c: ConversationData) => c.id === data.id)) {
              draft.items.unshift(data);
              if (typeof draft.total === 'number') draft.total++;
            }
          }
        });
      }

      // 2. Apply conversation updates from WebSocket
      for (const payload of conversationUpdates) {
        if (!payload.id || recentlyMutatedConversationIds.has(payload.id)) continue;

        // Detect full vs partial payload
        const isFull = 'conversationType' in payload && 'participants' in payload;

        if (isFull) {
          // Full conversation object — transform and replace in caches.
          // Guard: if the incoming payload somehow has empty assignees but the cache
          // already has assignees (e.g. race between DB query and WS delivery), keep
          // the cached assignees so they don't transiently disappear.
          const transformed = transformConversation(payload as unknown as BackendConversation);
          store.dispatch(
            conversationQueriesApi.util.updateQueryData('getConversationById', payload.id, (draft) => {
              if (!draft) return;
              const savedAssignees = !transformed.assignees?.length && draft.assignees?.length
                ? draft.assignees
                : undefined;
              // Socket payloads lack otherContacts (backend skips enrichment for performance).
              // Preserve the cached otherContacts so the shared-number switcher stays visible.
              const savedOtherContacts = !transformed.otherContacts?.length && draft.otherContacts?.length
                ? draft.otherContacts
                : undefined;
              Object.assign(draft, transformed);
              if (savedAssignees) draft.assignees = savedAssignees as typeof draft.assignees;
              if (savedOtherContacts) draft.otherContacts = savedOtherContacts;
            })
          );
          forEachConversationCache((draft) => {
            if (!draft?.items) return;
            const idx = draft.items.findIndex((c: ConversationData) => c.id === payload.id);
            if (idx !== -1) {
              const existing = draft.items[idx];
              const savedAssignees = !transformed.assignees?.length && existing.assignees?.length
                ? existing.assignees
                : undefined;
              const savedOtherContacts = !transformed.otherContacts?.length && existing.otherContacts?.length
                ? existing.otherContacts
                : undefined;
              Object.assign(draft.items[idx], transformed);
              if (savedAssignees) draft.items[idx].assignees = savedAssignees;
              if (savedOtherContacts) draft.items[idx].otherContacts = savedOtherContacts;
            }
          });
        } else {
          // Partial update — merge only the fields present
          const partial: Partial<ConversationData> = {};
          if ('lastMessage' in payload && payload.lastMessage !== undefined) partial.lastMessage = payload.lastMessage;
          if ('timestamp' in payload && payload.timestamp !== undefined) partial.timestamp = payload.timestamp as unknown as number;
          // Only apply unreadCount for conversations the user is NOT currently viewing.
          // The active conversation should stay read — the auto-mark-read effect and
          // handleConversationChange handle syncing with the backend on navigation.
          if ('unreadCount' in payload && payload.unreadCount !== undefined) {
            const currentActiveId = activeConversationIdRef.current;
            if (!currentActiveId || payload.id !== currentActiveId) {
              partial.unreadCount = payload.unreadCount;
            }
          }
          if ('isOpen' in payload && payload.isOpen !== undefined) partial.isOpen = payload.isOpen as boolean;
          if ('isActive' in payload && payload.isActive !== undefined) partial.isActive = payload.isActive as boolean;

          if (Object.keys(partial).length > 0) {
            store.dispatch(
              conversationQueriesApi.util.updateQueryData('getConversationById', payload.id, (draft) => {
                if (draft) Object.assign(draft, partial);
              })
            );
            forEachConversationCache((draft) => {
              if (!draft?.items) return;
              const idx = draft.items.findIndex((c: ConversationData) => c.id === payload.id);
              if (idx !== -1) {
                Object.assign(draft.items[idx], partial);
              }
            });
          }
        }
      }

      // 3. Apply contact consent updates
      for (const update of contactUpdates) {
        dispatch(
          contactsCoreApi.util.updateQueryData('getContactDetails', update.contactId, (draft) => {
            if (draft) {
              const record = draft as Record<string, unknown>;
              record.consentStatus = update.consentStatus;
              record.hasConsent = update.consentStatus === 'consented';
              if (update.consentStatus === 'opted_out') {
                record.optedOutAt = new Date().toISOString();
              }
            }
          })
        );
      }

      // 4. Single deduplicated tag invalidation
      if (tags.length > 0) {
        const seen = new Set<string>();
        const uniqueTags = tags.filter((tag) => {
          const key = `${tag.type}:${tag.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        dispatch(baseApi.util.invalidateTags(uniqueTags));
      }
    }

    function scheduleFlush() {
      if (!rafHandle) {
        rafHandle = requestAnimationFrame(flush);
      }
    }

    // --- Event handlers ---

    // Handler for new conversations
    const handleConversationCreated = (data: ConversationData) => {
      // If we're currently viewing this conversation (i.e., we just created it from
      // compose mode), skip cache update. The compose handler already did an
      // optimistic update and the mutation's own invalidatesTags handles the refetch.
      const currentActiveId = activeConversationIdRef.current;
      if (currentActiveId && currentActiveId === data.id) return;

      pendingCreations.push(data);
      scheduleFlush();
    };

    // Handler for conversation activity events (e.g., contact edited, archive, restore)
    const handleConversationActivity = (data: ConversationActivityPayload) => {
      const activity = data.activity;
      if (!activity) return;

      // Handle archive: directly remove conversation from all caches
      if (activity.subtype === 'conversation_archived') {
        forEachConversationCache((draft) => {
          if (!draft?.items) return;
          const idx = draft.items.findIndex((c: ConversationData) => c.id === activity.conversationId);
          if (idx !== -1) {
            draft.items.splice(idx, 1);
            if (typeof draft.total === 'number') draft.total--;
          }
        });
        return;
      }

      // Handle restore: invalidate lists so restored conversation appears
      if (activity.subtype === 'conversation_restored') {
        pendingTags.push({ type: 'Conversations', id: 'LIST' });
        pendingTags.push({ type: 'Conversations', id: 'INFINITE-LIST' });
        scheduleFlush();
        return;
      }

      // Handle assignment subtypes: direct cache patch instead of list refetch
      if (activity.subtype === 'contact_assigned' || activity.subtype === 'contact_claimed') {
        const convId = activity.conversationId;
        if (!convId || recentlyMutatedConversationIds.has(convId)) return;

        const assigneeId = activity.data?.assigneeId as string | undefined;
        const assigneeName = activity.data?.assigneeName as string | undefined;
        if (!assigneeId) return;

        const newAssignee = { id: assigneeId, name: assigneeName || '', email: '', avatar: '', color: null };

        // Phase 1: patch assignees field everywhere
        store.dispatch(
          conversationQueriesApi.util.updateQueryData('getConversationById', convId, (draft) => {
            if (draft && 'assignees' in draft) {
              const already = draft.assignees?.some((a) => a.id === assigneeId);
              if (!already) {
                draft.assignees = [...(draft.assignees || []), newAssignee];
              }
            }
          })
        );
        forEachConversationCache((draft) => {
          if (!draft?.items) return;
          const idx = draft.items.findIndex((c: ConversationData) => c.id === convId);
          if (idx !== -1) {
            const conv = draft.items[idx];
            const already = conv.assignees?.some((a) => a.id === assigneeId);
            if (!already) {
              conv.assignees = [...(conv.assignees || []), newAssignee];
            }
          }
        });

        return;
      }

      if (activity.subtype === 'contact_unassigned') {
        const convId = activity.conversationId;
        if (!convId || recentlyMutatedConversationIds.has(convId)) return;

        const unassignedUserId = activity.data?.unassignedUserId as string | undefined;
        if (!unassignedUserId) return;

        // Phase 1: remove assignee from all caches
        store.dispatch(
          conversationQueriesApi.util.updateQueryData('getConversationById', convId, (draft) => {
            if (draft && 'assignees' in draft) {
              draft.assignees = (draft.assignees || []).filter((a) => a.id !== unassignedUserId);
            }
          })
        );
        forEachConversationCache((draft) => {
          if (!draft?.items) return;
          const idx = draft.items.findIndex((c: ConversationData) => c.id === convId);
          if (idx !== -1) {
            draft.items[idx].assignees = (draft.items[idx].assignees || []).filter((a) => a.id !== unassignedUserId);
          }
        });

        return;
      }

      // Handle open/close status subtypes
      if (activity.subtype === 'conversation_closed' || activity.subtype === 'conversation_opened') {
        const convId = activity.conversationId;
        if (!convId || recentlyMutatedConversationIds.has(convId)) return;

        const isOpen = activity.subtype === 'conversation_opened';
        store.dispatch(
          conversationQueriesApi.util.updateQueryData('getConversationById', convId, (draft) => {
            if (draft) {
              Object.assign(draft, { isOpen, isActive: isOpen });
            }
          })
        );
        forEachConversationCache((draft) => {
          if (!draft?.items) return;
          const idx = draft.items.findIndex((c: ConversationData) => c.id === convId);
          if (idx !== -1) {
            Object.assign(draft.items[idx], { isOpen, isActive: isOpen });
          }
        });
        return;
      }

      // Handle contact_blocked / contact_unblocked: invalidate contact cache
      if (activity.subtype === 'contact_blocked' || activity.subtype === 'contact_unblocked') {
        const contactId = activity.data?.contactId as string | undefined;
        if (contactId) {
          pendingTags.push({ type: 'Contacts', id: contactId });
        }
        scheduleFlush();
        return;
      }

      // Handle contact_edited: tag invalidation (existing behavior)
      if (activity.subtype === 'contact_edited') {
        pendingTags.push({ type: 'Conversations', id: 'LIST' });
        if (activity.conversationId) {
          pendingTags.push({ type: 'Conversations', id: activity.conversationId });
        }
        const contactId = activity.data?.contactId as string | undefined;
        if (contactId && !recentlyUpdatedContactIds.has(contactId)) {
          pendingTags.push({ type: 'Contacts', id: contactId });
        }
        scheduleFlush();
        return;
      }

      // All other subtypes: no-op
    };

    // Handler for conversation updates (assignment changes, status, last message, etc.)
    const handleConversationUpdated = (data: ConversationUpdatedPayload) => {
      if (!data?.id) return;
      pendingConversationUpdates.push(data);
      scheduleFlush();
    };

    // Handler for organization-level consent updates
    const handleContactConsentUpdated = (data: ContactConsentUpdatedPayload) => {
      if (!data.contactId || !data.consentStatus) return;

      pendingContactUpdates.push(data);
      pendingTags.push({ type: 'Contacts', id: data.contactId });
      scheduleFlush();
    };

    on(ServerEvents.CONVERSATION_CREATED, handleConversationCreated);
    on(ServerEvents.CONVERSATION_UPDATED, handleConversationUpdated);
    on(ServerEvents.CONVERSATION_ACTIVITY, handleConversationActivity);
    on(ServerEvents.CONTACT_CONSENT_UPDATED, handleContactConsentUpdated);

    return () => {
      if (rafHandle) cancelAnimationFrame(rafHandle);
      off(ServerEvents.CONVERSATION_CREATED, handleConversationCreated);
      off(ServerEvents.CONVERSATION_UPDATED, handleConversationUpdated);
      off(ServerEvents.CONVERSATION_ACTIVITY, handleConversationActivity);
      off(ServerEvents.CONTACT_CONSENT_UPDATED, handleContactConsentUpdated);
    };
  }, [connected, on, off, dispatch]);
};
